import axios from "axios";
import { clearApiCache, createCacheAdapter } from "./cacheAdapter";

const normalizeBaseUrl = (raw) => {
  if (!raw) return "";
  const trimmed = String(raw).trim().replace(/\/+$/, "");
  // Client code uses `/api/...` paths; avoid `/api/api/...` when configured with `/api`.
  return trimmed.endsWith("/api") ? trimmed.slice(0, -4) : trimmed;
};

/** In dev, use relative URLs so CRA `proxy` in package.json forwards /api → localhost:5000 */
const resolveBaseURL = () => {
  const fromEnv = normalizeBaseUrl(process.env.REACT_APP_API_URL);
  if (fromEnv) return fromEnv;
  if (process.env.NODE_ENV === "development") {
    return `http://${window.location.hostname}:5000`;
  }
  return "https://attendance-webpage.onrender.com";
};

const api = axios.create({
  baseURL: resolveBaseURL(),
  withCredentials: true,
  adapter: createCacheAdapter(),
});

// REQUEST INTERCEPTOR
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// RESPONSE INTERCEPTOR
api.interceptors.response.use(
  (response) => {
    const method = String(response.config?.method || "get").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      clearApiCache();
    }
    return response;
  },

  (error) => {
    if (error.response) {
      const status = error.response.status;

      const url = error.config.url;

      // AUTH ROUTES
      const isAuthRoute = url.includes("/auth/login");

      // ATTENDANCE ROUTES
      const isAttendanceRoute = url.includes("/attendance");

      // CHANGE PASSWORD ROUTE
      const isChangePasswordRoute = url.includes("/profile/change-password");

      // RESET PASSWORD ROUTE
      const isResetPasswordRoute = url.includes("/reset-password");

      // DON'T AUTO LOGOUT
      // FOR VALIDATION ERRORS
      if (isChangePasswordRoute || isResetPasswordRoute) {
        return Promise.reject(error);
      }

      // DON'T REDIRECT
      // FOR LOGIN FAILURES
      if (isAuthRoute) {
        return Promise.reject(error);
      }

      // DON'T REDIRECT
      // FOR ATTENDANCE ERRORS
      if (isAttendanceRoute) {
        return Promise.reject(error);
      }

      // ONLY TOKEN/SESSION FAILURES
      if (status === 401 || status === 403) {
        const currentPath = window.location.pathname;
        localStorage.removeItem("admin_token");

        // AVOID REDIRECT LOOP
        const isPublicAuthPage = 
          currentPath === "/login" || 
          currentPath === "/admin/login" || 
          currentPath === "/set-password" ||
          currentPath === "/reset-password";

        if (!isPublicAuthPage) {
          const isViewingAdminPortal = currentPath.startsWith("/admin");

          window.location.href = isViewingAdminPortal ? "/admin/login" : "/login";
        }
      }
    }

    return Promise.reject(error);
  },
);

export default api;
